import React from "react";
import { Button } from "react-bootstrap";

// Shared grid pagination — the pinned footer used by the Territory / Sales-Rep
// search (DynamicSearch), now shared by Customers and Quotes so every grid looks
// and behaves the same. Pages are 0-based. Place it OUTSIDE the scrolling results
// region (as a flex sibling) so it stays pinned at the bottom of the shell while
// only the rows scroll.
//
// Props:
//   currentPage  0-based current page index
//   totalPages   total number of pages
//   totalCount   total matching rows (for the "of N" text)
//   showFrom/showTo  1-based row range currently shown
//   onGoToPage(page) navigate to a 0-based page
//   extra        optional node rendered next to the "Showing…" text (e.g. a
//                rows-per-page selector)
//   hasNext      OPTIONAL. When provided (not undefined), the pager runs in
//                "simple" mode: Prev/Next only, driven by currentPage/hasNext
//                instead of totalPages. Use this when the true total is unknown
//                (e.g. a source that can't cheaply count) so paging still works
//                without a "of N" total or jump-to-page numbers. When omitted,
//                the classic numbered pager (needs totalPages) renders as before.
//                In simple mode, pass totalCount={null} to drop the "of N" text.
export default function GridPagination({
  currentPage,
  totalPages,
  totalCount,
  showFrom,
  showTo,
  onGoToPage,
  maxVisible = 5,
  extra = null,
  hasNext = undefined,
}) {
  const simple = hasNext !== undefined;

  let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
  const endPage = Math.min(totalPages, startPage + maxVisible);
  startPage = Math.max(0, endPage - maxVisible);
  const pageNumbers = [];
  for (let i = startPage; i < endPage; i++) pageNumbers.push(i);

  // Show the pager row when there's somewhere to go. Simple mode: a prev page
  // exists or a next page might. Classic mode: more than one known page.
  const showPager = simple ? (currentPage > 0 || hasNext) : totalPages > 1;
  const nextDisabled = simple ? !hasNext : currentPage >= totalPages - 1;

  // Three zones: Prev pinned hard left, Next pinned hard right, and everything
  // informational (row count, rows-per-page, page numbers) centred between them.
  // The side zones share the same flex basis as the middle so the middle stays
  // optically centred even though Prev/Next differ slightly in width.
  // `1 0 auto` — the side zones grow to balance each other but never shrink, so
  // the Prev/Next buttons keep their full size no matter how wide the middle
  // group gets. On mobile the word labels drop away (d-none d-md-inline) and the
  // buttons collapse to bare ‹ / › chevrons, which is what keeps the row aligned
  // on a narrow screen instead of wrapping into a jumble.
  const sideZone = { flex: "1 0 auto" };

  return (
    <div
      className="d-flex justify-content-between align-items-center gap-2"
      style={{ flexShrink: 0, padding: "10px 0 0", marginTop: 10, borderTop: "1px solid #dee2e6" }}
    >
      {/* LEFT — Prev (plus first-page jump in classic mode) */}
      <div className="d-flex align-items-center justify-content-start gap-1" style={sideZone}>
        {showPager && (
          <>
            {!simple && (
              <Button size="sm" variant="outline-primary" disabled={currentPage === 0} onClick={() => onGoToPage(0)}>
                &laquo;
              </Button>
            )}
            <Button size="sm" variant="outline-primary" disabled={currentPage === 0} onClick={() => onGoToPage(currentPage - 1)} aria-label="Previous page">
              &lsaquo;<span className="d-none d-md-inline">&nbsp;Prev</span>
            </Button>
          </>
        )}
      </div>

      {/* MIDDLE — result count, rows-per-page, and (desktop, classic mode) page numbers */}
      <div className="d-flex align-items-center justify-content-center gap-2 gap-md-3 flex-wrap" style={{ flex: "1 1 auto", minWidth: 0 }}>
        <span style={{ fontSize: "13px", color: "#555" }}>
          Showing {showFrom}&ndash;{showTo}{totalCount != null ? <> of {totalCount}</> : null} results
        </span>
        {extra}
        {showPager && !simple && (
          <span style={{ fontSize: "13px", color: "#555" }}>
            Page {currentPage + 1} of {totalPages}
          </span>
        )}

        {/* Numbered pages — desktop only; mobile keeps just Prev/Next.
            Hidden in simple mode (total unknown → no page numbers). */}
        {showPager && !simple && (
          <div className="d-none d-lg-flex align-items-center gap-1">
            {startPage > 0 && <span style={{ fontSize: "12px", color: "#888", padding: "0 4px" }}>&hellip;</span>}
            {pageNumbers.map((p) => (
              <Button
                key={p}
                size="sm"
                variant={p === currentPage ? "primary" : "outline-primary"}
                onClick={() => onGoToPage(p)}
                style={{ minWidth: "32px" }}
              >
                {p + 1}
              </Button>
            ))}
            {endPage < totalPages && <span style={{ fontSize: "12px", color: "#888", padding: "0 4px" }}>&hellip;</span>}
          </div>
        )}
      </div>

      {/* RIGHT — Next (plus last-page jump in classic mode) */}
      <div className="d-flex align-items-center justify-content-end gap-1" style={sideZone}>
        {showPager && (
          <>
            <Button size="sm" variant="outline-primary" disabled={nextDisabled} onClick={() => onGoToPage(currentPage + 1)} aria-label="Next page">
              <span className="d-none d-md-inline">Next&nbsp;</span>&rsaquo;
            </Button>
            {!simple && (
              <Button size="sm" variant="outline-primary" disabled={currentPage >= totalPages - 1} onClick={() => onGoToPage(totalPages - 1)}>
                &raquo;
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
